import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${import.meta.env.VITE_API_BASE_URL}/documentos`;

const ESTADO_BADGE = {
  pendiente:  'bg-yellow-100 text-yellow-700 border-yellow-200',
  verificado: 'bg-green-100  text-green-700  border-green-200',
  error:      'bg-red-100    text-red-700    border-red-200',
};

const fmtFecha = iso =>
  iso ? new Date(iso).toLocaleString('es-PE', { dateStyle: 'short', timeStyle: 'short' }) : '—';

// Un pedido es gratuito si monto_pagado === 0 (guardado como 0.00 en BD)
const esGratuito = pedido =>
  pedido.monto_pagado !== null && pedido.monto_pagado !== undefined && Number(pedido.monto_pagado) === 0;

// ── Sub-componente: Fila de pedido ────────────────────────────────────────────
const FilaPedido = ({ pedido, onProcesar, onGuardarNota }) => {
  const [expandido, setExpandido]         = useState(false);
  const [nota, setNota]                   = useState(pedido.notas_admin || '');
  const [monto, setMonto]                 = useState(pedido.monto_pagado || '');
  const [procesando, setProcesando]       = useState(false);
  const [guardandoNota, setGuardandoNota] = useState(false);

  const gratuito = esGratuito(pedido);

  const handleProcesar = async () => {
    const confirmMsg = gratuito
      ? `¿Procesar el pedido GRATUITO ${pedido.token_pedido}? Este docente no realizó pago (primer pedido).`
      : `¿Procesar el pedido ${pedido.token_pedido}? Esto invocará Python y generará el ZIP.`;
    if (!window.confirm(confirmMsg)) return;
    setProcesando(true);
    await onProcesar(pedido.token_pedido, gratuito ? 0 : monto);
    setProcesando(false);
  };

  const handleGuardarNota = async () => {
    setGuardandoNota(true);
    await onGuardarNota(pedido.token_pedido, nota);
    setGuardandoNota(false);
  };

  return (
    <div className={`border rounded-xl overflow-hidden mb-3 shadow-sm ${gratuito ? 'border-green-200' : 'border-gray-200'}`}>

      {/* ── Cabecera ── */}
      <div
        className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors ${gratuito ? 'bg-green-50' : 'bg-white'}`}
        onClick={() => setExpandido(v => !v)}
      >
        {/* Badge de estado */}
        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${ESTADO_BADGE[pedido.estado_pago] || ESTADO_BADGE.error}`}>
          {pedido.estado_pago}
        </span>

        {/* Badge GRATUITO — muy visible para que no pases por alto */}
        {gratuito && (
          <span className="text-xs font-bold px-2 py-0.5 rounded-full border bg-emerald-100 text-emerald-700 border-emerald-300 flex-shrink-0">
            🎁 GRATUITO
          </span>
        )}

        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-800 text-sm truncate">{pedido.docente}</p>
          <p className="text-xs text-gray-400 font-mono">{pedido.token_pedido}</p>
        </div>
        <p className="text-xs text-gray-400 hidden sm:block">{fmtFecha(pedido.fecha_creacion)}</p>
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${expandido ? 'rotate-180' : ''}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {/* ── Detalle expandible ── */}
      {expandido && (
        <div className="border-t border-gray-100 bg-gray-50 px-4 py-4 space-y-4">

          {/* Aviso de gratuito */}
          {gratuito && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-xs text-emerald-700 font-medium">
              🎁 Este es el primer pedido del docente — fue procesado de forma gratuita. No hubo pago de por medio.
            </div>
          )}

          {/* Grid de datos */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            {[
              ['Institución',       pedido.institucion],
              ['WhatsApp',          pedido.whatsapp_contacto],
              ['Correo',            pedido.correo_contacto],
              ['Titular Yape',      gratuito ? '— (pedido gratuito)' : pedido.yape_titular],
              ['N° Operación',      gratuito ? '— (pedido gratuito)' : pedido.numero_operacion],
              ['Cód. Verificación', gratuito ? '—' : (pedido.codigo_verificacion || '—')],
              ['Verificado en',     fmtFecha(pedido.fecha_verificacion)],
            ].map(([label, val]) => (
              <div key={label}>
                <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">{label}</p>
                <p className="text-gray-800 font-medium break-all">{val || '—'}</p>
              </div>
            ))}
          </div>

          {/* ── Monto ── */}
          <div>
            <label className="block text-xs text-gray-400 font-medium uppercase tracking-wide mb-1">
              Monto recibido (S/)
            </label>
            {pedido.estado_pago === 'verificado' || gratuito ? (
              <p className="text-gray-800 font-semibold">
                {gratuito
                  ? <span className="text-emerald-600">S/ 0.00 — Primer pedido gratuito</span>
                  : pedido.monto_pagado ? `S/ ${Number(pedido.monto_pagado).toFixed(2)}` : '—'
                }
              </p>
            ) : (
              <input
                type="number"
                step="0.01"
                min="0"
                value={monto}
                onChange={e => setMonto(e.target.value)}
                placeholder="Ej. 25.00"
                className="w-full border border-gray-200 rounded-lg p-2 text-sm focus:ring-2 focus:ring-indigo-300 outline-none bg-white"
              />
            )}
          </div>

          {/* ── Notas admin ── */}
          <div>
            <label className="block text-xs text-gray-400 font-medium uppercase tracking-wide mb-1">
              Notas del admin
            </label>
            <textarea
              value={nota}
              onChange={e => setNota(e.target.value)}
              rows={2}
              placeholder="Observaciones internas..."
              className="w-full border border-gray-200 rounded-lg p-2 text-sm resize-none focus:ring-2 focus:ring-indigo-300 outline-none bg-white"
            />
            <button
              onClick={handleGuardarNota}
              disabled={guardandoNota}
              className="mt-1 text-xs text-indigo-600 hover:text-indigo-800 font-medium disabled:opacity-50"
            >
              {guardandoNota ? 'Guardando...' : 'Guardar nota'}
            </button>
          </div>

          {/* ── Botón procesar ── */}
          {pedido.estado_pago !== 'verificado' && (
            <button
              onClick={handleProcesar}
              disabled={procesando}
              className={`w-full py-2.5 text-white rounded-lg font-semibold text-sm transition-colors disabled:opacity-50 flex items-center justify-center gap-2
                ${gratuito
                  ? 'bg-emerald-600 hover:bg-emerald-700'
                  : 'bg-green-600 hover:bg-green-700'
                }`}
            >
              {procesando ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                  </svg>
                  Generando documentos...
                </>
              ) : gratuito ? (
                '🎁 Procesar pedido gratuito'
              ) : (
                '✅ Verificar y procesar pedido'
              )}
            </button>
          )}

          {pedido.estado_pago === 'verificado' && (
            <p className="text-center text-sm text-green-600 font-medium">
              ✓ Documentos generados el {fmtFecha(pedido.fecha_verificacion)}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// ── Página admin principal ────────────────────────────────────────────────────
const AdminPage = () => {
  const [pedidos, setPedidos]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [filtro, setFiltro]           = useState('todos');
  const [busqueda, setBusqueda]       = useState('');
  const [adminKey, setAdminKey]       = useState('');
  const [autenticado, setAutenticado] = useState(false);
  const [toast, setToast]             = useState('');

  const mostrarToast = msg => {
    setToast(msg);
    setTimeout(() => setToast(''), 4000);
  };

  const cargarPedidos = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${API}/admin/pedidos`, {
        headers: { 'x-admin-key': adminKey },
      });
      if (!data.success) throw new Error(data.message);
      setPedidos(data.pedidos);
    } catch (err) {
      setError(
        err.response?.status === 403
          ? 'Clave de administrador incorrecta.'
          : err.message || 'Error al cargar pedidos.'
      );
    } finally {
      setLoading(false);
    }
  }, [adminKey]);

  useEffect(() => {
    if (autenticado) cargarPedidos();
  }, [autenticado, cargarPedidos]);

  const handleProcesar = async (token, monto) => {
    try {
      const { data } = await axios.post(
        `${API}/admin/procesar/${token}`,
        { monto_pagado: monto ?? null },
        { headers: { 'x-admin-key': adminKey } }
      );
      mostrarToast(`✅ ZIP generado. WhatsApp: ${data.whatsapp || '—'}`);
      cargarPedidos();
    } catch (err) {
      mostrarToast(`❌ Error: ${err.response?.data?.message || err.message}`);
    }
  };

  const handleGuardarNota = async (token, nota) => {
    try {
      await axios.patch(
        `${API}/admin/notas/${token}`,
        { notas_admin: nota },
        { headers: { 'x-admin-key': adminKey } }
      );
      mostrarToast('Nota guardada.');
    } catch {
      mostrarToast('❌ Error al guardar nota.');
    }
  };

  const pedidosFiltrados = pedidos.filter(p => {
    const matchFiltro   = filtro === 'todos' || filtro === 'gratuitos'
      ? filtro === 'gratuitos' ? esGratuito(p) : true
      : p.estado_pago === filtro;
    const matchBusqueda = !busqueda ||
      p.token_pedido.toLowerCase().includes(busqueda.toLowerCase()) ||
      (p.docente || '').toLowerCase().includes(busqueda.toLowerCase()) ||
      (p.numero_operacion || '').includes(busqueda);
    return matchFiltro && matchBusqueda;
  });

  const conteos = {
    todos:      pedidos.length,
    pendiente:  pedidos.filter(p => p.estado_pago === 'pendiente').length,
    verificado: pedidos.filter(p => p.estado_pago === 'verificado').length,
    error:      pedidos.filter(p => p.estado_pago === 'error').length,
    gratuitos:  pedidos.filter(p => esGratuito(p)).length,
  };

  // ── Pantalla de login ──────────────────────────────────────────────────────
  if (!autenticado) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-sm">
          <h1 className="text-2xl font-black text-gray-800 mb-1">Panel Admin</h1>
          <p className="text-sm text-gray-400 mb-6">Ingresa tu clave de acceso</p>
          <input
            type="password"
            value={adminKey}
            onChange={e => setAdminKey(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && setAutenticado(true)}
            placeholder="Clave secreta"
            className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-indigo-400 outline-none mb-4"
          />
          <button
            onClick={() => setAutenticado(true)}
            className="w-full py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
          >
            Entrar
          </button>
        </div>
      </div>
    );
  }

  // ── Panel principal ────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50">

      {/* Toast */}
      {toast && (
        <div className="fixed top-4 right-4 z-50 bg-gray-900 text-white text-sm px-4 py-3 rounded-xl shadow-xl max-w-xs">
          {toast}
        </div>
      )}

      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-black text-gray-800">Panel de Pedidos</h1>
          <p className="text-xs text-gray-400">{pedidos.length} pedidos en total</p>
        </div>
        <button
          onClick={cargarPedidos}
          disabled={loading}
          className="text-sm text-indigo-600 hover:text-indigo-800 font-medium disabled:opacity-50"
        >
          {loading ? 'Cargando...' : '↻ Actualizar'}
        </button>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6">

        {/* Filtros */}
        <div className="flex flex-wrap gap-2 mb-4">
          {Object.entries(conteos).map(([estado, n]) => (
            <button
              key={estado}
              onClick={() => setFiltro(estado)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors
                ${filtro === estado
                  ? estado === 'gratuitos'
                    ? 'bg-emerald-600 text-white'
                    : 'bg-indigo-600 text-white'
                  : 'bg-white text-gray-600 border border-gray-200 hover:border-indigo-300'
                }`}
            >
              {estado === 'gratuitos' ? '🎁 ' : ''}
              {estado.charAt(0).toUpperCase() + estado.slice(1)} ({n})
            </button>
          ))}
        </div>

        {/* Búsqueda */}
        <input
          type="text"
          value={busqueda}
          onChange={e => setBusqueda(e.target.value)}
          placeholder="Buscar por token, docente o N° operación..."
          className="w-full border border-gray-200 rounded-xl p-3 text-sm mb-4 focus:ring-2 focus:ring-indigo-300 outline-none bg-white shadow-sm"
        />

        {/* Estados */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-600 mb-4">
            {error}
          </div>
        )}

        {loading && (
          <div className="text-center py-12 text-gray-400 text-sm">Cargando pedidos...</div>
        )}

        {!loading && pedidosFiltrados.length === 0 && (
          <div className="text-center py-12 text-gray-400 text-sm">
            {busqueda ? 'No se encontraron resultados.' : 'No hay pedidos en este estado.'}
          </div>
        )}

        {!loading && pedidosFiltrados.map(pedido => (
          <FilaPedido
            key={pedido.token_pedido}
            pedido={pedido}
            onProcesar={handleProcesar}
            onGuardarNota={handleGuardarNota}
          />
        ))}
      </div>
    </div>
  );
};

export default AdminPage;
